import { useMemo, useCallback, useImperativeHandle, forwardRef, useEffect, useState } from 'react'
import OriginalJoyride, { Props as JoyrideProps, TooltipRenderProps } from 'react-joyride'
import { GlobalStyles } from '@mui/material'
import { Tabs } from '../../layout/MainLayout'

import { getSteps } from '../../tour/steps'
import { TutorialStep, ExtendedHelpers } from '../../tour/types'
import { cleanupDependenciesForStepTransition } from '../../tour/stepManagement/dependencyManager'

import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'
import { useModalContext } from '../../contexts/ModalProvider'
import { useTutorialContext } from '../../contexts/TutorialContext'
import useTourController from '../../hooks/tour/useTourController'
import { useTourSpotlight } from '../../hooks/tour/useTourSpotlight'
import { useTourKeyboardControls } from '../../hooks/tour/useTourKeyboardControls'

import JoyrideTooltipRenderer from './tooltips/JoyrideTooltipRenderer'
import WelcomeScreen from './common/WelcomeScreen'
import TourSpotlight from './spotlight/TourSpotlight'

// Vimeo embed codes
const vimeoEmbeds = {
  patternBuilderIntro:
    '<div style="padding:56.25% 0 0 0;position:relative;"><iframe src="https://player.vimeo.com/video/1109155057?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&amp;title=0&amp;portrait=0&amp;transcript=0&amp;controls=1&amp;pip=0&amp;byline=0&autoplay=1&muted=1" frameborder="0" allow="autoplay; encrypted-media;" referrerpolicy="strict-origin-when-cross-origin" style="position:absolute;top:0;left:0;width:100%;height:100%;" title="pattern_overview"></iframe></div><script src="https://player.vimeo.com/api/player.js"></script>',
  backtestIntro:
    '<div style="padding:56.25% 0 0 0;position:relative;"><iframe src="https://player.vimeo.com/video/1109155065?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&amp;title=0&amp;portrait=0&amp;transcript=0&amp;controls=1&amp;pip=0&amp;byline=0&autoplay=1&muted=1" frameborder="0" allow="autoplay; encrypted-media;" referrerpolicy="strict-origin-when-cross-origin" style="position:absolute;top:0;left:0;width:100%;height:100%;" title="backtest_overview"></iframe></div><script src="https://player.vimeo.com/api/player.js"></script>',
  alertsIntro:
    '<div style="padding:56.25% 0 0 0;position:relative;"><iframe src="https://player.vimeo.com/video/1109155077?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&amp;title=0&amp;portrait=0&amp;transcript=0&amp;controls=1&amp;pip=0&amp;byline=0&autoplay=1&muted=1" frameborder="0" allow="autoplay; encrypted-media;" referrerpolicy="strict-origin-when-cross-origin" style="position:absolute;top:0;left:0;width:100%;height:100%;" title="alerts_overview"></iframe></div><script src="https://player.vimeo.com/api/player.js"></script>',
}

// New video mapping system that supports both types
const videoMap: { [key: string]: { src: any; type: 'mp4' | 'vimeo' } } = {
  patternBuilderIntro: { src: vimeoEmbeds.patternBuilderIntro, type: 'vimeo' },
  backtestIntro: { src: vimeoEmbeds.backtestIntro, type: 'vimeo' },
  alertsIntro: { src: vimeoEmbeds.alertsIntro, type: 'vimeo' },
}

export interface StrategyTutorialRef {
  startTour: () => void
}
export interface StrategyTutorialProps {
  onComplete?: () => void
  pageContext?: 'patternBuilder' | 'backtest' | 'alerts'
  showcaseContext?: any
  allowHighlightedInteractions?: boolean
}

const StrategyTutorial = forwardRef<StrategyTutorialRef, StrategyTutorialProps>(
  (
    {
      onComplete,
      pageContext = 'patternBuilder',
      showcaseContext = null,
      allowHighlightedInteractions = false,
    },
    ref
  ) => {
    const [isTourActive, setIsTourActive] = useState(false)

    const { usermeta, tradingHouse, setActiveTab } = useApplicationSuiteContext()
    const modalContext = useModalContext()
    const { setIsTutorialActive, setActiveTutorialType } = useTutorialContext()

    const originalTutorialSteps = useMemo(() => {
      return getSteps(pageContext)
    }, [pageContext])

    const endTourSession = useCallback(() => {
      setIsTourActive(false)
      setIsTutorialActive(false)
      setActiveTutorialType(null)
    }, [setIsTutorialActive, setActiveTutorialType])

    const onCompleteWrapper = useCallback(() => {
      endTourSession()
      if (pageContext === 'alerts') {
        setActiveTab({ tab: Tabs.Alerts })
      } else if (pageContext === 'backtest') {
        setActiveTab({ tab: Tabs.Showcase })
      }
      if (onComplete) {
        onComplete()
      }
    }, [onComplete, endTourSession, pageContext, setActiveTab])

    const {
      run,
      setRun,
      stepIndex,
      setStepIndex,
      joyrideSteps,
      setRawJoyrideHelpers,
      handleJoyrideCallback,
      exposedStartTour: exposedStartTourFromController,
      jumpTargetRef,
      createCompatibleExtendedHelpers,
    } = useTourController({
      initialStepsFromConfig: originalTutorialSteps,
      onComplete: onCompleteWrapper,
      showcaseContext,
      modalContext,
      setActiveTab,
    })

    const setStepIndexWithCleanup = useCallback(
      async (newIndex: number) => {
        const fromIndex = stepIndex
        if (fromIndex === newIndex) {
          return
        }

        const helpers = createCompatibleExtendedHelpers()

        if (!helpers) {
          setStepIndex(newIndex)
          return
        }

        await cleanupDependenciesForStepTransition(
          fromIndex,
          newIndex,
          originalTutorialSteps,
          modalContext,
          showcaseContext,
          setActiveTab
        )

        await new Promise((resolve) => setTimeout(resolve, 50))

        setStepIndex(newIndex)
      },
      [
        stepIndex,
        setStepIndex,
        originalTutorialSteps,
        createCompatibleExtendedHelpers,
        modalContext,
        showcaseContext,
        setActiveTab,
      ]
    )

    const exposedStartTour = useCallback(() => {
      setIsTourActive(true)
      setIsTutorialActive(true)
      setActiveTutorialType(pageContext)
      exposedStartTourFromController()
    }, [
      exposedStartTourFromController,
      setIsTourActive,
      setIsTutorialActive,
      setActiveTutorialType,
      pageContext,
    ])

    useImperativeHandle(ref, () => ({ startTour: exposedStartTour }), [exposedStartTour])

    const spotlightPosition = useTourSpotlight(
      run,
      isTourActive,
      joyrideSteps,
      stepIndex,
      originalTutorialSteps,
      createCompatibleExtendedHelpers
    )
    useTourKeyboardControls(
      run,
      isTourActive,
      stepIndex,
      setStepIndexWithCleanup,
      joyrideSteps,
      originalTutorialSteps
    )

    useEffect(() => {
      if (isTourActive) {
        const originalOverflow = document.body.style.overflow
        const originalPaddingRight = document.body.style.paddingRight
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
        document.body.style.overflow = 'hidden'
        document.body.style.paddingRight = `${scrollbarWidth}px`
        document.body.classList.add('tour-backdrop-active')
        return () => {
          document.body.style.overflow = originalOverflow
          document.body.style.paddingRight = originalPaddingRight
          document.body.classList.remove('tour-backdrop-active')
        }
      }
    }, [isTourActive])

    const memoizedTooltipRenderer = useCallback(
      (props: TooltipRenderProps) => {
        const compatibleHelpersForTooltip = createCompatibleExtendedHelpers()
        const originalStep = originalTutorialSteps[props.index] as TutorialStep

        if (originalStep?.isWelcomeScreen) {
          const videoConfig = originalStep.video ? videoMap[originalStep.video] : undefined
          return (
            <WelcomeScreen
              title={originalStep.title}
              content={originalStep.content}
              videoSrc={videoConfig?.src}
              videoType={videoConfig?.type}
              onStart={() => setStepIndex(props.index + 1)}
              onExit={() => {
                setRun(false)
                onCompleteWrapper()
              }}
            />
          )
        }

        return (
          <JoyrideTooltipRenderer
            {...props}
            pageContext={pageContext}
            usermeta={usermeta}
            tradingHouse={tradingHouse}
            allOriginalStepsForMenu={originalTutorialSteps}
            compatibleJoyrideHelpers={compatibleHelpersForTooltip as ExtendedHelpers}
            jumpTargetRefFromController={jumpTargetRef}
            setRunController={setRun}
            setStepIndexController={setStepIndexWithCleanup}
            onCompleteController={onCompleteWrapper}
          />
        )
      },
      [
        pageContext,
        usermeta,
        tradingHouse,
        originalTutorialSteps,
        createCompatibleExtendedHelpers,
        jumpTargetRef,
        setRun,
        setStepIndexWithCleanup,
        onCompleteWrapper,
        setStepIndex,
      ]
    )

    const currentStep = originalTutorialSteps[stepIndex] as TutorialStep
    const shouldAllowInteraction = currentStep?.allowInteraction ?? allowHighlightedInteractions

    const joyrideProps: JoyrideProps = {
      steps: joyrideSteps,
      run,
      stepIndex,
      continuous: true,
      showSkipButton: false,
      showProgress: false,
      getHelpers: setRawJoyrideHelpers,
      tooltipComponent: memoizedTooltipRenderer,
      callback: handleJoyrideCallback,
      debug: false,
      disableOverlay: true,
      spotlightClicks: shouldAllowInteraction,
      disableScrolling: true,
      disableCloseOnEsc: true,
      disableOverlayClose: true,
      hideBackButton: true,
      styles: {
        options: { zIndex: 100002, arrowColor: '#2D2D2D' },
        tooltip: { padding: 0, backgroundColor: 'transparent', zIndex: 100001 },
        spotlight: { display: 'none' },
        overlay: { display: 'none' },
      },
    }

    return (
      <>
        <GlobalStyles
          styles={{
            '[data-tour-element], [data-tour-element] *, .__floater, .__floater *': {
              boxSizing: 'border-box',
            },
            '[data-placement*="top"] .__floater__arrow': {
              marginBottom: '-2px !important',
            },
            '[data-placement*="bottom"] .__floater__arrow': {
              marginTop: '-2px !important',
            },
            '[data-placement*="left"] .__floater__arrow': {
              marginRight: '-2px !important',
              right: '2px !important',
              left: 'auto !important',
            },
            '[data-placement*="right"] .__floater__arrow': {
              marginLeft: '-2px !important',
            },
          }}
        />
        <TourSpotlight
          isTourActive={isTourActive}
          spotlightPosition={spotlightPosition}
          allowInteraction={shouldAllowInteraction}
        />
        <OriginalJoyride {...joyrideProps} />
      </>
    )
  }
)

StrategyTutorial.displayName = 'StrategyTutorial'

export default StrategyTutorial
